import Icon from "@/components/Icon/Icon";
import { Grid, VStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";
import { Breadcrumb } from "@/components/Navigation/Breadcrumb";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { CartItemList } from "./-components/CartItemList";
import { HandoverNotes } from "./-components/HandoverNotesForm";
import { OrderSummary } from "./-components/OrderSummary";
import { useShippingForm } from "./-components/ShippingForm/-hooks/useShippingForm";
import { ShippingForm } from "./-components/ShippingForm/ShippingForm";
import { useCart } from "./-hooks/useCart";
import { useHandoverNotes } from "./-hooks/useHandoverNotes";

export const Route = createFileRoute("/_staff/marketplace/cart/")({
  component: CartPage,
});

function CartPage() {
  const shippingForm = useShippingForm();
  const handoverNotes = useHandoverNotes();
  const cart = useCart();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.marketplace",
  });

  const breadcrumbs = [
    {
      label: t("deviceMarket"),
      link: {
        to: "/marketplace/store/devices" as const,
      },
    },
    {
      label: t("cart"),
    },
  ];

  if (cart.isLoading) {
    return <ViewportLoader />;
  }

  if (cart.items.length === 0) {
    return (
      <VStack className="container mx-auto gap-10 py-8">
        <Breadcrumb breadcrumbs={breadcrumbs} />
        <Alert>
          <Icon iconName="AlertCircle" colorVariant="warning" />
          <AlertTitle>{t("emptyCart")}</AlertTitle>
          <AlertDescription>{t("emptyCartDescription")}</AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <div className="mt-8">
        <Grid
          columns={2}
          columnGap={32}
          rowGap={48}
          gridTemplateColumns="1fr 420px"
        >
          <VStack className="gap-8">
            <CartItemList cart={cart} />
            <ShippingForm
              placeState={shippingForm.placeState}
              addressState={shippingForm.addressState}
            />
            <HandoverNotes {...handoverNotes} />
          </VStack>
          <OrderSummary
            cart={cart}
            handoverMemo={handoverNotes.handoverMemo}
            shippingFormValues={shippingForm.values}
            validateShippingForm={() =>
              shippingForm.validateShippingForm(shippingForm.values)
            }
          />
        </Grid>
      </div>
    </div>
  );
}
